import { Box } from "@mantine/core";
import { useAtomValue } from "jotai";
import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import { EditIcon } from "components/Icons/Edit";
import Text from "components/Text";

import { DigitalJourneyRoute } from "app/DigitalJourneyAppFlow/constants";
import { sessionIdAtom } from "atoms/sessionId";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useEventTracking } from "hooks/useEventTracking";
import { replaceSessionInAppRoute } from "utils/routeHelper";

interface DJSummarySectionProps {
  title: string;
  pageName: string;
  pageRoute: DigitalJourneyRoute;
  values: Array<{ label: string; value: any }>;
}

const DJSummarySection: React.FC<DJSummarySectionProps> = ({ title, pageName, pageRoute, values }) => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const navigate = useNavigate();
  const sessionId = useAtomValue(sessionIdAtom);

  const onEdit = () => {
    trackEvent({
      name: "Digital Journey Claim Summary - edit",
      properties: {
        pageName,
      },
    });

    navigate(replaceSessionInAppRoute(pageRoute, sessionId), { state: "FROM_SUMMARY" });
  };

  return (
    <>
      <Flex as="section" direction="row" justify="space-between" mb={24} gap={8}>
        <Text as="h2" sizeVariant="md" weight={700}>
          {title}
        </Text>
        <Button
          typeVariant="link"
          leftIcon={<EditIcon fill={colors.grey[70]} />}
          sx={{
            "&": {
              fontWeight: 700,
              textDecoration: "none",
              justifySelf: "end",
              alignSelf: "start",
              color: colors.grey[70],
            },
          }}
          onClick={onEdit}
        >
          {t("global.edit")}
        </Button>
      </Flex>
      <Box
        component="section"
        sx={{
          "&": {
            display: "grid",
            gap: "0.5rem 0.5rem",
            gridTemplateColumns: "auto auto",
          },
        }}
      >
        {values.map(({ label, value }) => (
          <Fragment key={label}>
            {label && (
              <Text sizeVariant="md" style={{ color: colors.grey[70] }}>
                {label}
              </Text>
            )}
            <Text
              sx={{
                "&": {
                  justifySelf: "end",
                  textAlign: "right",
                },
              }}
              sizeVariant="md"
            >
              {value}
            </Text>
          </Fragment>
        ))}
      </Box>
    </>
  );
};

export default DJSummarySection;
